/* eslint-disable react-hooks/exhaustive-deps */
import { useDisclosure } from '@chakra-ui/react';
import { IoMdRefresh as RefreshIcon } from 'react-icons/io';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from 'hooks/useCustomQuery';
import getCompany from 'API/Company/getCompany';
import getAppAuthById from 'API/AppAuth/getAppAuthById';
import { useSelector } from 'react-redux';
// import getCompanyServices from '../../../../API/getCompanySevices';
import useServiceWizard from 'hooks/useServiceWizard';
import objChangeDetection from 'utils/objChangeDetection';
import { PlatformEnums } from 'utils/platforms';
import ServiceWizardMsAuth from '../../services/new/ServiceWizardMsAuth';
import ServiceWizardSyncUsers from '../../services/new/ServiceWizardSyncUsers';
import CreateService from './CreateService';

import { Error as ErrorView } from '@/components/index';
import { nullError } from 'components/error';
import { Button } from 'components/v4';
import { useDashboardMenu } from 'context/DashboardMenuContext';
import { DefinedAttributesProvider } from 'pages/services/serviceprofiles/vendorDefined/DefinedAttributesContext';
import AddVendorDefined from './components/VendorCard/VendorDefined/AddVendorDefined';
import AddEightByEight from './add/AddEightByEight';
import LicenceEntitlements from './add/LicenceEntitlements';
import {
    QUERY_CACHE_TIME,
    REFETCH_INTERVAL,
    MICROSOFT_GRAPH,
} from './constants';
import PartnerConfirmationModal from 'pages/dashboards/v5/add/PartnerConfirmationModal';
import { useDashboardCompanyServices } from 'hooks/useCustomQuery';
import {
    shouldBypassDashboardShapeCache,
    readDashboardShapeCache,
    writeDashboardShapeCache,
} from './dashboardShapeCache';
import DashboardHome from './home/DashboardHome';
import useDashboardHomeReady from './home/useDashboardHomeReady';
import AccountSummary, {
    SERVICE_STATUS_ID,
} from './home/accountSummary/AccountSummary';
import DashboardNotifications from './home/notifications/DashboardNotifications';
import {
    getNotificationRoute,
    NOTIFICATION_DESTINATION,
} from './home/notifications/notificationTypes';
import SetupProgress from './home/setupProgress/SetupProgress';
import { SETUP_STEP_KEYS } from './home/setupProgress/setupSteps';
import { isSameServiceHealth } from './components/helpers/serviceHealth';
import { NUMBERS_PAGE_DRAWER } from 'pages/telephony/numbers/constants';

// (2635) This dashboard's route, which the service wizard returns to.
const DASHBOARD_PATH = '/dashboard-services/new';

const ServiceDashboardPage = () => {
    // Service Wizard Flow
    const serviceWizard = useServiceWizard({ dashboardPath: DASHBOARD_PATH });
    const navigate = useNavigate();
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { darkMode } = useSelector((state) => state.settings);
    const dashboardShapeCacheUser = useSelector(
        (state) => state.login?.email || state.login?.user || '',
    );
    const {
        isOpen: isPartnerModalOpen,
        onOpen: onPartnerModalOpen,
        onClose: onPartnerModalClose,
    } = useDisclosure();

    // Data
    const [services, setServices] = useState(null);
    const [cachedAppAuths, setCachedAppAuths] = useState([]);
    const prevServicesRef = useRef([]);
    const lastServicesSnapshotKeyRef = useRef('');
    const traceSnapshotRef = useRef('');
    const [hasCachedShape, setHasCachedShape] = useState(false);
    const bypassDashboardShapeCache = shouldBypassDashboardShapeCache();
    const { isQueryDisabled, setIsQueryDisabled } = useDashboardMenu();

    // (3133/3153) What the service sections report about attention and app
    // auth failures, for the account summary flag and the notifications.
    const [serviceHealth, setServiceHealth] = useState(null);
    const handleServiceHealthChange = useCallback((next) => {
        setServiceHealth((prev) =>
            isSameServiceHealth(prev, next) ? prev : next,
        );
    }, []);

    // (3135) The service "+ Add now" was used for, so Add New Service opens
    // straight to its confirmation.
    const [pendingAddProfile, setPendingAddProfile] = useState(null);

    useEffect(() => {
        setServiceHealth(null);
    }, [currentCompany]);

    useEffect(() => {
        if (serviceWizard.flow === 'Start') setPendingAddProfile(null);
    }, [serviceWizard.flow]);

    const {
        data: companyData,
        isLoading: isCompanyLoading,
        isInitialLoading: isCompanyInitialLoading,
    } = useQuery({
        queryKey: ['company', currentCompany],
        queryFn: () => getCompany(currentCompany),
        cacheTime: QUERY_CACHE_TIME,
        refetchInterval: false,
        enabled: !isQueryDisabled && !!currentCompany,
        notifyOnChangeProps: ['data', 'isLoading', 'isInitialLoading'],
        refetchOnWindowFocus: false,
        retry: false,
    });

    // `['company', currentCompany]` is shared with Searchbar/CompanyStatus/
    // useURLRewrite (useGetCurrentCompany), so this query can resolve from
    // their cache write instead of its own queryFn — sync via effect on
    // `companyData` itself so serviceWizard.company populates either way.
    useEffect(() => {
        if (companyData) {
            serviceWizard.setCompany(companyData);
        }
    }, [companyData]);

    const dashboardServicesQueryKey = [
        'dashboardServices',
        serviceWizard.flow,
        `company: ${currentCompany}`,
    ];

    const {
        data: dashboardData,
        isLoading: isDashboardLoading,
        isFetching: isDashboardFetching,
        isFetchedAfterMount: isDashboardFetchedAfterMount,
    } = useDashboardCompanyServices({
        currentCompany,
        flow: serviceWizard.flow,
        queryKey: dashboardServicesQueryKey,
        options: {
            cacheTime: QUERY_CACHE_TIME,
            refetchInterval:
                !isQueryDisabled && !!currentCompany ? REFETCH_INTERVAL : false,
            enabled: !isQueryDisabled && !!currentCompany,
            notifyOnChangeProps: ['data', 'isLoading', 'isFetching'],
            refetchOnWindowFocus: false,
        },
    });
    const isDashboardBusy = isDashboardLoading || isDashboardFetching;
    // The query key includes the flow, so on entering Add New Service the
    // services can still be cached from an earlier visit — wait for a fresh
    // fetch before showing which services are already added.
    const isExistingServicesPending =
        !isQueryDisabled && !!currentCompany && !isDashboardFetchedAfterMount;

    // Reset 8x8-specific wizard steps when the company changes; the new
    // company may not support 8x8, and profile data is stale.
    useEffect(() => {
        if (
            serviceWizard.flow === 'AddEightByEight' ||
            serviceWizard.flow === 'LicenceEntitlements'
        ) {
            serviceWizard.setFlow('CreateService');
            serviceWizard.setSelectedServiceProfile(null);
            serviceWizard.setEightByEightCompanyDetails(null);
            serviceWizard.setHasCreateServiceError(null);
        }
    }, [currentCompany]);

    useEffect(() => {
        if (!import.meta.env.DEV) return;
        if (!window.location.search.includes('perf=1')) return;

        const snapshot = {
            flow: serviceWizard.flow,
            currentCompany,
            dashboardCacheMode: bypassDashboardShapeCache
                ? 'bypass'
                : 'default',
            isCompanyLoading,
            hasCachedShape,
            dashboardServices: dashboardData?.services?.length || 0,
            dashboardAppAuths: dashboardData?.appAuths?.length || 0,
            renderedServices: services?.length ?? null,
            isDashboardLoading,
            isDashboardFetching,
            isDashboardBusy,
            isQueryDisabled,
        };
        const nextSnapshot = JSON.stringify(snapshot);

        if (traceSnapshotRef.current === nextSnapshot) return;
        traceSnapshotRef.current = nextSnapshot;

        console.info(`[DASH:trace] ServiceDashboardPage ${nextSnapshot}`);
    }, [
        currentCompany,
        bypassDashboardShapeCache,
        dashboardData,
        hasCachedShape,
        isCompanyLoading,
        isDashboardBusy,
        isDashboardFetching,
        isDashboardLoading,
        isQueryDisabled,
        serviceWizard.flow,
        services,
    ]);

    useEffect(() => {
        lastServicesSnapshotKeyRef.current = '';
        prevServicesRef.current = [];
        setHasCachedShape(false);
        setCachedAppAuths([]);

        if (bypassDashboardShapeCache) {
            setServices(null);
            return;
        }

        const cachedShape = readDashboardShapeCache(
            currentCompany,
            dashboardShapeCacheUser,
        );

        if (cachedShape.services.length || cachedShape.appAuths.length) {
            prevServicesRef.current = cachedShape.services;
            setHasCachedShape(true);
            setCachedAppAuths(cachedShape.appAuths);
            setServices(cachedShape.services);
            return;
        }

        lastServicesSnapshotKeyRef.current = '';
        setServices(null);
    }, [
        currentCompany,
        dashboardShapeCacheUser,
        bypassDashboardShapeCache,
        serviceWizard.flow,
    ]);

    useEffect(() => {
        if (!dashboardData && isDashboardBusy) {
            // If we already have a cached shape, keep rendering it until the
            // consolidated payload hydrates.
            setServices((prev) => {
                if (prev?.length) return prev;
                lastServicesSnapshotKeyRef.current = '';
                return null;
            });
            return;
        }

        const next = dashboardData?.services || [];
        const nextAppAuths = dashboardData?.appAuths || [];

        // If the consolidated payload did not change in any way that affects rendering,
        // skip updating state to avoid re-rendering the entire service list.
        const nextServicesKey = next
            .map((s) => {
                const level = s?.statusDisplay?.statusDisplayLevel;
                const detail =
                    s?.statusDisplay?.statusDisplayDetail ||
                    s?.statusDisplay?.statusDisplayText ||
                    '';
                return `${s?.id}:${s?.serviceType}:${s?.status}:${level}:${detail}`;
            })
            .join('|');
        const nextAppAuthsKey = nextAppAuths
            .map((a) => {
                const level = a?.statusDisplay?.statusDisplayLevel;
                const detail =
                    a?.statusDisplay?.statusDisplayDetail ||
                    a?.statusDisplay?.statusDisplayText ||
                    '';
                return `${a?.id}:${a?.appAuthTypeID}:${a?.platformID}:${a?.status}:${level}:${detail}`;
            })
            .join('|');
        const nextSnapshotKey = `${nextServicesKey}::${nextAppAuthsKey}`;
        if (nextSnapshotKey === lastServicesSnapshotKeyRef.current) {
            return;
        }
        lastServicesSnapshotKeyRef.current = nextSnapshotKey;
        setHasCachedShape(false);
        setCachedAppAuths([]);

        if (next.length || nextAppAuths.length) {
            if (!bypassDashboardShapeCache) {
                writeDashboardShapeCache(
                    currentCompany,
                    dashboardShapeCacheUser,
                    dashboardData,
                );
            }
            const result = objChangeDetection(prevServicesRef.current, next);
            prevServicesRef.current = next;
            setServices((prev) => {
                if (
                    Array.isArray(prev) &&
                    prev.length === result.length &&
                    prev.every((item, idx) => item === result[idx])
                ) {
                    return prev;
                }
                return result;
            });
        } else {
            if (!bypassDashboardShapeCache) {
                writeDashboardShapeCache(
                    currentCompany,
                    dashboardShapeCacheUser,
                    {
                        services: [],
                        appAuths: [],
                    },
                );
            }
            prevServicesRef.current = [];
            setServices((prev) =>
                Array.isArray(prev) && !prev.length ? prev : [],
            );
        }
    }, [
        bypassDashboardShapeCache,
        currentCompany,
        dashboardData,
        dashboardShapeCacheUser,
        isDashboardBusy,
    ]);

    // User would need to click on refresh button if idle for more than 2 minutes
    useEffect(() => {
        if (!isQueryDisabled) {
            const timer = setTimeout(() => {
                setIsQueryDisabled(true);
            }, 120000);

            return () => {
                clearTimeout(timer);
            };
        }

        // reset refresh on navigation
        return () => {
            setIsQueryDisabled(false);
        };
    }, [isQueryDisabled]);

    const handleRefresh = () => {
        setIsQueryDisabled(false);
    };

    const handleAddService = (serviceProfile = null) => {
        if (isCompanyLoading) return;
        setPendingAddProfile(serviceProfile);
        if (companyData?.partnerID) {
            serviceWizard.setFlow('CreateService');
        } else {
            onPartnerModalOpen();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onPartnerModalClose();
        serviceWizard.setFlow('CreateService');
    };

    // 2629: the consolidated dashboard payload's appAuths entry for
    // Microsoft Teams does not carry msGraphAppInstanceID/lastAccessSuccess.
    // /appauth/company/{companyID} doesn't return these values either, so
    // once we find the Teams appAuth in the dashboard payload we fetch its
    // full record by ID (/appauth/{appAuthID}) to decide whether Migrate to
    // New Sync applies.
    const msTeamsAppAuth = (dashboardData?.appAuths || []).find(
        (appAuth) =>
            appAuth?.appAuthTypeID === MICROSOFT_GRAPH &&
            appAuth?.platformID === PlatformEnums.TEAMS,
    );

    const { data: msTeamsAppAuthDetail } = useQuery({
        queryKey: ['appAuthById', msTeamsAppAuth?.id],
        queryFn: async () => {
            const res = await getAppAuthById(msTeamsAppAuth.id);
            return res.data;
        },
        cacheTime: QUERY_CACHE_TIME,
        refetchInterval: false,
        enabled: !isQueryDisabled && !!msTeamsAppAuth?.id,
        notifyOnChangeProps: ['data'],
        refetchOnWindowFocus: false,
        retry: false,
    });

    const isMigrateToNewSync =
        !!msTeamsAppAuth &&
        !msTeamsAppAuthDetail?.msGraphAppInstanceID &&
        !!msTeamsAppAuthDetail?.lastAccessSuccess &&
        msTeamsAppAuthDetail?.statusDisplay?.statusDisplayLevel === 0;

    // (2635) Same "unknown" test the setup progress bar uses: no payload and
    // no cached shape yet.
    const isDashboardHomeReady = useDashboardHomeReady({
        companyId: currentCompany,
        isServicesPending: services === null && !cachedAppAuths.length,
        isCompanyPending: isCompanyInitialLoading,
    });

    if (!currentCompany) {
        return <ErrorView error={nullError} />;
    }

    // (3154) Where each setup milestone sends the user. "Make calls" completes
    // on its own, so it has no destination. Get numbers asks the Numbers page
    // to open its Available Number Blocks drawer; the page does that itself
    // once loaded, the dashboard never opens it.
    const handleSetupStepAction = (stepKey) => {
        switch (stepKey) {
            case SETUP_STEP_KEYS.ADD_SERVICE:
                handleAddService();
                break;
            case SETUP_STEP_KEYS.GET_NUMBERS:
                navigate('/numbers', {
                    state: {
                        openDrawer: NUMBERS_PAGE_DRAWER.AVAILABLE_NUMBER_BLOCKS,
                    },
                });
                break;
            case SETUP_STEP_KEYS.MANAGE_NUMBERS:
                navigate('/numbers');
                break;
            case SETUP_STEP_KEYS.ENABLE_USERS:
                navigate('/users');
                break;
            default:
                break;
        }
    };

    // Same array DashboardServices renders from: live once the payload lands,
    // the cached shape until then.
    const appAuths = dashboardData
        ? dashboardData?.appAuths || []
        : cachedAppAuths;

    // Only mounted service sections report health, so a company with none
    // has nothing to flag.
    const hasServiceCards = (services?.length || 0) + appAuths.length > 0;
    const visibleServiceHealth = hasServiceCards ? serviceHealth : null;

    // The Microsoft auth the company banner checked: Teams, or failing that
    // any Microsoft Graph auth that isn't GCC High (#2335).
    const msCommercialAuth =
        appAuths.find(
            (a) =>
                a?.appAuthTypeID === MICROSOFT_GRAPH &&
                a?.platformID === PlatformEnums.TEAMS,
        ) ||
        appAuths.find(
            (a) =>
                a?.appAuthTypeID === MICROSOFT_GRAPH &&
                a?.platformID !== PlatformEnums.GCC,
        );

    // (3153) Warnings about services point at this page, so they scroll to
    // Service status; the legacy sync one starts the upgrade; the rest go to
    // where the problem gets fixed.
    const handleNotificationSelect = (notification) => {
        if (
            notification.destination === NOTIFICATION_DESTINATION.UPGRADE_SYNC
        ) {
            serviceWizard.microsoftAuth();
            return;
        }
        const route = getNotificationRoute(notification.destination);
        if (route) {
            navigate(route.path, route.state ? { state: route.state } : {});
            return;
        }
        document
            .getElementById(SERVICE_STATUS_ID)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <>
            {serviceWizard.flow === 'Start' && (
                <>
                    <DashboardHome
                        isLoading={!isDashboardHomeReady}
                        setupProgress={
                            <SetupProgress
                                services={services}
                                appAuths={appAuths}
                                companyApproved={companyData?.approved}
                                onStepAction={handleSetupStepAction}
                                // Learn how will open the step's help &
                                // training; until then it goes straight to
                                // where the step gets done.
                                onLearnHow={handleSetupStepAction}
                            />
                        }
                        main={
                            <AccountSummary
                                onAddService={handleAddService}
                                notifications={
                                    <DashboardNotifications
                                        serviceHealth={visibleServiceHealth}
                                        company={companyData}
                                        msAuth={msCommercialAuth}
                                        showLegacySyncRetirement={
                                            isMigrateToNewSync
                                        }
                                        onSelect={handleNotificationSelect}
                                    />
                                }
                                dashboardServicesProps={{
                                    serviceWizard,
                                    services,
                                    cachedAppAuths,
                                    dashboardData,
                                    companyData,
                                    isDashboardLoading: isDashboardBusy,
                                    msTeamsAppAuthDetail,
                                    onServiceHealthChange:
                                        handleServiceHealthChange,
                                }}
                            />
                        }
                    />
                    {/* US 1917: Adding Sevices to Partner Companies */}
                    <PartnerConfirmationModal
                        colorScheme="messenger"
                        darkMode={darkMode}
                        handleSubmit={handleSubmit}
                        isOpen={isPartnerModalOpen}
                        onClose={onPartnerModalClose}
                        partnerName={companyData?.name}
                    />

                    {isQueryDisabled && (
                        <Button
                            borderRadius="2xl"
                            onClick={handleRefresh}
                            style={{
                                position: 'fixed',
                                bottom: 50,
                                right: 50,
                                zIndex: 1000,
                            }}
                            leftIcon={
                                <RefreshIcon size={20} className="text-white" />
                            }>
                            <span className="flex items-center text-white">
                                Refresh
                            </span>
                        </Button>
                    )}
                </>
            )}

            {serviceWizard.flow === 'CreateService' && (
                <CreateService
                    serviceWizard={serviceWizard}
                    existingServices={dashboardData?.services}
                    isExistingServicesPending={isExistingServicesPending}
                    autoConfirmServiceProfileID={pendingAddProfile?.id}
                    isNewDashboard
                />
            )}

            {serviceWizard.flow === 'SelectiveSyncUsers' && (
                <ServiceWizardSyncUsers serviceWizard={serviceWizard} />
            )}

            {serviceWizard.flow === 'SelectiveSyncUsersEdit' && (
                <ServiceWizardSyncUsers serviceWizard={serviceWizard} edit />
            )}

            {serviceWizard.flow === 'MsAuth' && (
                <ServiceWizardMsAuth serviceWizard={serviceWizard} />
            )}

            {serviceWizard.flow === 'VendorDefined' && (
                <DefinedAttributesProvider>
                    <AddVendorDefined serviceWizard={serviceWizard} />
                </DefinedAttributesProvider>
            )}

            {serviceWizard.flow === 'AddEightByEight' && (
                <AddEightByEight serviceWizard={serviceWizard} />
            )}

            {serviceWizard.flow === 'LicenceEntitlements' && (
                <LicenceEntitlements serviceWizard={serviceWizard} />
            )}
        </>
    );
};

export default ServiceDashboardPage;
